import fetch from 'saddlebag-http';
import { v4 as uuidv4 } from 'uuid';

import type { BOARD_TYPE } from '../constants';
import type {
  CodeshareData,
  FlightEntryData,
  FlightStatus,
} from '@skyscanner-internal/landing-pages-shared-types/types-shared/ArrivalsDepartures';

interface ServerCommonData {
  airlineId: string;
  flightNumber: string;
  airlineName: string;
  status: FlightStatus;
  codeshares: CodeshareData[];
  flightTrackerUrl?: string;
}

interface ServerArrivalData extends ServerCommonData {
  scheduledArrivalTime: string;
  departureAirportName: string;
  localisedScheduledArrivalTime: string;
  localisedEstimatedArrivalTime: string;
  localisedScheduledDepartureTime: string;
  arrivalTerminal: string;
  arrivalGate: string;
}

interface ServerDepartureData extends ServerCommonData {
  scheduledDepartureTime: string;
  arrivalAirportName: string;
  localisedScheduledDepartureTime: string;
  localisedEstimatedDepartureTime: string;
  departureTerminal: string;
  boardingGate: string;
}

interface FlightInfoJson {
  arrivals: ServerArrivalData[];
  departures: ServerDepartureData[];
}

const isValidFlightInfo = (item: ServerArrivalData | ServerDepartureData) =>
  !!item.airlineName;

const transformCommonFlightInfoData = (entry: ServerCommonData) => ({
  airlineImage: `https://images.skyscnr.com/images/airlines/favicon/${entry.airlineId}.png`,
  airline: entry.airlineName,
  flightCode: entry.flightNumber,
  status: entry.status,
  codeshares: entry.codeshares,
  ...(entry.flightTrackerUrl && { flightTrackerUrl: entry.flightTrackerUrl }),
});

const transformFlightInfoData = (flightInfoJson: FlightInfoJson) => {
  const transformedData: {
    arrivalsList: FlightEntryData[];
    departuresList: FlightEntryData[];
  } = { arrivalsList: [], departuresList: [] };

  if (flightInfoJson?.arrivals?.length > 0) {
    transformedData.arrivalsList = flightInfoJson.arrivals
      .filter(isValidFlightInfo)
      .map((entry) => ({
        ...transformCommonFlightInfoData(entry),
        id: uuidv4(),
        location: entry.departureAirportName,
        time: entry.localisedScheduledArrivalTime,
        estimatedTime: entry.localisedEstimatedArrivalTime,
        departureTime: entry.localisedScheduledDepartureTime,
        terminal: entry.arrivalTerminal,
        gate: entry.arrivalGate,
      }));
  }

  if (flightInfoJson?.departures?.length > 0) {
    transformedData.departuresList = flightInfoJson.departures
      .filter(isValidFlightInfo)
      .map((entry) => ({
        ...transformCommonFlightInfoData(entry),
        id: uuidv4(),
        location: entry.arrivalAirportName,
        time: entry.localisedScheduledDepartureTime,
        estimatedTime: entry.localisedEstimatedDepartureTime,
        departureTime: entry.localisedScheduledDepartureTime,
        terminal: entry.departureTerminal,
        gate: entry.boardingGate,
      }));
  }

  return transformedData;
};

const getLiveFlightData = async (
  iataCode: string,
  locale: string,
  board: keyof typeof BOARD_TYPE,
  options?: { market?: string; includeFlightTrackerUrls?: boolean },
) => {
  try {
    const searchParams = new URLSearchParams({ locale });
    if (options?.market) {
      searchParams.append('market', options.market);
    }
    if (options?.includeFlightTrackerUrls) {
      searchParams.append('includeFlightTrackerUrls', 'true');
    }

    const url = `/g/arrival-departure-svc/api/airports/${iataCode}/${board.toLowerCase()}?${searchParams}`;

    const response = await fetch(url);

    return transformFlightInfoData((await response.json()) as FlightInfoJson);
  } catch (e) {
    throw new Error(
      `Failed to fetch ${board} flight results for ${iataCode}, locale: ${locale}`,
    );
  }
};

export default getLiveFlightData;
